import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal, Space, Typography, Select, Checkbox, Button, Tooltip, Spin,
  Empty, Alert, message, DatePicker,
} from 'antd';
import {
  CalendarOutlined, ReloadOutlined, ScheduleOutlined, ThunderboltOutlined,
  ApiOutlined, PauseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { listConnections, listDagitRuns, getSchedulerStatus } from '../services/dataPlatformService';
import SchedulerControl, { connectorSensorKey } from './SchedulerControl';
import { openDagsterRun, isDagitEnabled } from '../services/dagsterLink';
import { useTheme } from '../contexts/ThemeContext';

const CARD_BORDER = '1px solid var(--theme-border, #f0f0f0)';
const DISPLAY_INLINE_BLOCK = 'inline-block';
const THEME_TEXT_ICON = 'var(--theme-textIcon, #8c8c8c)';

dayjs.extend(relativeTime);

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Workspace-wide run pull. The /syncs/dagit/runs endpoint caps at 500; 200
// covers several weeks for a typical tenant. We surface a banner when the
// fetch actually hits this ceiling so users know history may be clipped.
const RUNS_FETCH_LIMIT = 200;

// Per-(connection × schedule-series) projection cap. A pathological cron
// (e.g. `* * * * *` over a year) would otherwise emit half a million points.
const PROJECTION_CAP = 4000;

const MS_PER_DAY = 864e5;
const MS_PER_MINUTE = 60000;
// Default charted window: this many days back / ahead of now.
const DEFAULT_PAST_DAYS = 30;
const DEFAULT_FUTURE_DAYS = 7;
// Default time-range picker span either side of today.
const DEFAULT_RANGE_DAYS = 7;
// Weekend shading is skipped for windows longer than this (it would be clutter).
const WEEKEND_BAND_MAX_DAYS = 45;
const WEEKEND_BAND_GUARD = 400;
const SATURDAY = 6;
const DEFAULT_CHART_W = 900;
const MIN_CHART_W = 200;
const MIN_BAR_WIDTH = 3;       // px: a run bar never renders thinner than this
const BAR_RADIUS = 3;
const TICK_HIT_PX = 3;         // px tolerance when hit-testing a scheduled tick
const TICK_WIDTH = 2.5;
const TICK_RADIUS = 1.25;
const SUB_ROW_INDENT = 20;
const ROW_INDENT = 10;
const FONT_WEIGHT_SUB = 400;
const FONT_WEIGHT_ROW = 600;
// Axis granularity cut-offs (window length in days).
const AXIS_DAILY_MAX_DAYS = 31;
const AXIS_WEEKLY_MAX_DAYS = 90;
const AXIS_TICK_GUARD = 2000;
// Cron field bounds and shape.
const CRON_FIELD_COUNT = 5;
const CRON_MINUTE_MAX = 59;
const CRON_HOUR_MAX = 23;
const CRON_DOM_MAX = 31;
const CRON_MONTH_MAX = 12;
const CRON_DOW_MAX = 7;        // 7 is accepted as an alias of Sunday (0)

// Gantt geometry.
const LABEL_W = 330;   // left label gutter (wide enough for the per-row Scheduler control)
const ROW_H = 26;      // height of one chart row
const HEADER_H = 30;   // time-axis header height
const BAR_H = 12;      // run bar thickness
const TICK_H = 14;     // scheduled-firing tick height

// One run as a chart span, or null when it has no usable start time or lies
// entirely outside the charted window.
function runToSpan(r, windowStartMs, windowEndMs) {
  const iso = r.start_time || r.creation_time;
  if (!iso) {return null;}
  const startMs = new Date(iso).getTime();
  if (Number.isNaN(startMs) || startMs > windowEndMs) {return null;}
  const endMs = r.end_time ? new Date(r.end_time).getTime() : Date.now();
  if (endMs < windowStartMs) {return null;} // entirely before window
  return {
    startMs, endMs, status: r.status, triggeredBy: r.triggered_by,
    runId: r.id, connector: r.connector,
  };
}

// The charted window in epoch ms. Falls back to the last 30 days → next 7
// days when the picker has no value.
function windowBoundsMs(range) {
  return [
    range && range[0] ? range[0].valueOf() : Date.now() - DEFAULT_PAST_DAYS * MS_PER_DAY,
    range && range[1] ? range[1].valueOf() : Date.now() + DEFAULT_FUTURE_DAYS * MS_PER_DAY,
  ];
}

/**
 * ScheduleTimelineDialog — full-screen Gantt of every connection's schedule.
 *
 * One row per connection (plus a sub-row for each per-stream schedule
 * override). Across a user-selected window (default: last 30 days → next 7
 * days):
 *   • Bars     — actual Dagster sync runs (scheduled + manual), drawn at
 *                their real start→end, coloured by status. Click to open in
 *                Dagit.
 *   • Ticks    — projected cron firings across the whole window. Blue ahead
 *                of "now", grey behind it (instantaneous, so drawn as ticks).
 *
 * Rendered on <canvas> so a dense schedule (hourly crons × many connections)
 * stays smooth — the marks never become thousands of DOM nodes.
 *
 * Props: open: boolean, onClose: () => void
 */
export default function ScheduleTimelineDialog({ open, onClose }) {
  const [connections, setConnections] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hitLimit, setHitLimit] = useState(false);
  // Live state of the Dagster SENSOR daemon + per-connector schedule sensors,
  // so the user can tell at a glance whether scheduled syncs can actually
  // fire. null = not yet loaded / fetch failed.
  const [scheduler, setScheduler] = useState(null);

  const [range, setRange] = useState(() => [
    dayjs().subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day'),
    dayjs().add(DEFAULT_RANGE_DAYS, 'day').endOf('day'),
  ]);
  const [filterConns, setFilterConns] = useState([]); // [] = all
  const [filterStatus, setFilterStatus] = useState(''); // '' = any (runs)
  const [showProjections, setShowProjections] = useState(true);
  const [showRuns, setShowRuns] = useState(true);

  // Loads once when the dialog opens (and on manual Refresh). No polling —
  // the user hits Refresh to pull newer runs.
  const refresh = useCallback(async () => {
    if (!open) {return;}
    setLoading(true);
    try {
      const [c, r, s] = await Promise.all([
        listConnections(),
        listDagitRuns({ limit: RUNS_FETCH_LIMIT }).catch(() => []),
        getSchedulerStatus().catch(() => null),
      ]);
      setConnections(c || []);
      setRuns(r || []);
      setScheduler(s);
      setHitLimit((r || []).length >= RUNS_FETCH_LIMIT);
    } catch (err) {
      message.error(`Failed to load schedules: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => { if (open) {refresh();} }, [open, refresh]);

  const [windowStartMs, windowEndMs] = windowBoundsMs(range);

  // ── Projected cron firings per connection ──────────────────────────
  // For each connection: the union of all its tables' effective firing times
  // (drawn on the collapsed connection row) plus a per-table breakdown
  // (revealed when the connection is expanded). Occurrences are computed once
  // per distinct cron and shared across tables that inherit the same cron.
  const schedByConn = useMemo(
    () => computeSchedByConn(connections, windowStartMs, windowEndMs),
    [connections, windowStartMs, windowEndMs],
  );

  // ── Actual runs grouped by connection ──────────────────────────────
  const runsByConn = useMemo(() => {
    const out = {};
    for (const r of runs) {
      const span = runToSpan(r, windowStartMs, windowEndMs);
      if (span) {
        if (!out[r.connection_id]) {out[r.connection_id] = [];}
        out[r.connection_id].push(span);
      }
    }
    return out;
  }, [runs, windowStartMs, windowEndMs]);

  // Connections whose per-table schedules are expanded (set of connection id).
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = useCallback((connId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(connId)) {next.delete(connId);} else {next.add(connId);}
      return next;
    });
  }, []);

  // ── Build Gantt rows ───────────────────────────────────────────────
  // One row per connection; clicking an expandable connection reveals a
  // sub-row per table (stream) showing that table's own effective schedule.
  const ganttRows = useMemo(
    () => buildGanttRows(
      connections, schedByConn, runsByConn, filterConns, filterStatus, showProjections, showRuns, expanded,
    ),
    [connections, schedByConn, runsByConn, filterConns, filterStatus, showProjections, showRuns, expanded],
  );

  // ── Summary stats ──────────────────────────────────────────────────
  const nowMs = Date.now();
  const stats = useMemo(() => {
    let upcoming = 0;
    for (const sched of Object.values(schedByConn)) {
      for (const t of sched.unionTicks) {if (t >= nowMs) {upcoming += 1;}}
    }
    const runCount = Object.values(runsByConn).reduce((n, a) => n + a.length, 0);
    const scheduled = connections.filter((c) => buildEffectiveSchedules(c).length > 0).length;
    return { upcoming, runCount, scheduledConns: scheduled, manualConns: connections.length - scheduled };
  }, [schedByConn, runsByConn, connections, nowMs]);

  const manualConnNames = useMemo(
    () => connections.filter((c) => buildEffectiveSchedules(c).length === 0).map((c) => c.name),
    [connections],
  );

  const connOptions = connections.map((c) => ({ value: c.id, label: `${c.name} · ${c.connector_name}` }));

  // Per-connector schedule-sensor lookup (keyed by the underscored connector
  // form) + daemon health, so each connection row can show its Scheduler
  // status and a Start/Stop control.
  const sensorByConnector = useMemo(() => {
    const m = {};
    for (const s of (scheduler && scheduler.sensors) || []) {m[s.connector] = s;}
    return m;
  }, [scheduler]);
  const daemonHealthy = !!(scheduler && scheduler.daemon && scheduler.daemon.healthy);

  let chartBody;
  if (loading) {
    chartBody = <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>;
  } else if (ganttRows.length === 0) {
    chartBody = (
      <Empty
        style={{ marginTop: 64 }}
        description={connections.length === 0 ? 'No connections configured yet.' : 'Nothing to chart in the selected window.'}
      />
    );
  } else {
    chartBody = (
      <GanttChart
        rows={ganttRows}
        windowStartMs={windowStartMs}
        windowEndMs={windowEndMs}
        onToggleExpand={toggleExpand}
        sensorByConnector={sensorByConnector}
        daemonHealthy={daemonHealthy}
        onSchedulerChanged={setScheduler}
      />
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable
      width="100vw"
      style={{ top: 0, paddingBottom: 0, maxWidth: '100vw' }}
      styles={{
        content: { minHeight: '100vh', borderRadius: 0 },
        body: { maxHeight: 'calc(100vh - 120px)', overflow: 'hidden', paddingTop: 8, display: 'flex', flexDirection: 'column' },
      }}
      destroyOnHidden
      title={
        <Space align="center">
          <CalendarOutlined style={{ color: 'var(--theme-primary, #1677ff)' }} />
          <span>Schedule Timeline</span>
        </Space>
      }
    >
      <Text type="secondary" style={{ display: 'block', fontSize: 12.5, marginBottom: 12 }}>
        Bars are actual sync runs (click to open in Dagit); ticks are projected cron firings.
        Click a connection to expand its per-table schedules. Cron in UTC, shown in your local time.
      </Text>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <StatCard icon={<ScheduleOutlined />} color="#1677ff" label="Upcoming scheduled" value={stats.upcoming} />
        <StatCard icon={<ThunderboltOutlined />} color="#fa8c16" label="Runs in window" value={stats.runCount} />
        <StatCard icon={<ApiOutlined />} color="#52c41a" label="Scheduled connections" value={stats.scheduledConns} />
        <StatCard
          icon={<PauseCircleOutlined />}
          color="#8c8c8c"
          label="Manual-only"
          value={stats.manualConns}
          tooltip={manualConnNames.length ? manualConnNames.join(', ') : 'None'}
        />
      </div>

      {/* Toolbar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
        padding: '10px 12px', background: 'var(--theme-backgroundSecondary, #fafafa)', border: CARD_BORDER,
        borderRadius: 10, marginBottom: 10,
      }}>
        <Tooltip title="Window to chart. Defaults to last 7 days → next 7 days.">
          <RangePicker
            value={range}
            onChange={(v) => v && v[0] && v[1] && setRange(v)}
            allowClear={false}
            presets={[
              { label: 'Default (−7d / +7d)', value: [dayjs().subtract(7, 'day').startOf('day'), dayjs().add(7, 'day').endOf('day')] },
              { label: 'Next 7 days', value: [dayjs().startOf('day'), dayjs().add(7, 'day').endOf('day')] },
              { label: 'Next 30 days', value: [dayjs().startOf('day'), dayjs().add(30, 'day').endOf('day')] },
              { label: 'Last 7 days', value: [dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day')] },
              { label: 'Last 30 days', value: [dayjs().subtract(30, 'day').startOf('day'), dayjs().endOf('day')] },
              { label: 'Last 90 days', value: [dayjs().subtract(90, 'day').startOf('day'), dayjs().endOf('day')] },
            ]}
            style={{ minWidth: 260 }}
          />
        </Tooltip>
        <Select
          mode="multiple"
          allowClear
          placeholder="All connections"
          value={filterConns}
          onChange={setFilterConns}
          options={connOptions}
          maxTagCount="responsive"
          style={{ minWidth: 220 }}
          showSearch
          optionFilterProp="label"
        />
        <Select
          allowClear
          placeholder="Any run status"
          value={filterStatus || undefined}
          onChange={(v) => setFilterStatus(v || '')}
          style={{ minWidth: 150 }}
          options={[
            { value: 'SUCCESS', label: 'Success' },
            { value: 'FAILURE', label: 'Failure' },
            { value: 'STARTED', label: 'Running' },
            { value: 'QUEUED', label: 'Queued' },
            { value: 'CANCELED', label: 'Canceled' },
          ]}
        />
        <span style={{ width: 1, height: 22, background: '#e8e8e8' }} />
        <Checkbox checked={showProjections} onChange={(e) => setShowProjections(e.target.checked)}>Projections</Checkbox>
        <Checkbox checked={showRuns} onChange={(e) => setShowRuns(e.target.checked)}>Actual runs</Checkbox>
        <span style={{ flex: 1 }} />
        <Button icon={<ReloadOutlined />} onClick={refresh}>Refresh</Button>
      </div>

      {/* Legend */}
      <Space wrap size={[18, 4]} style={{ marginBottom: 8 }}>
        <Legend swatch={<span style={{ display: DISPLAY_INLINE_BLOCK, width: 4, height: 14, background: '#1677ff', borderRadius: 2 }} />} label="Upcoming scheduled" />
        <Legend swatch={<span style={{ display: DISPLAY_INLINE_BLOCK, width: 4, height: 14, background: '#bfbfbf', borderRadius: 2 }} />} label="Past scheduled" />
        <Legend swatch={<span style={{ display: DISPLAY_INLINE_BLOCK, width: 18, height: 10, background: '#52c41a', borderRadius: 3 }} />} label="Run succeeded" />
        <Legend swatch={<span style={{ display: DISPLAY_INLINE_BLOCK, width: 18, height: 10, background: '#ff4d4f', borderRadius: 3 }} />} label="Run failed" />
        <Legend swatch={<span style={{ display: DISPLAY_INLINE_BLOCK, width: 18, height: 10, background: '#1677ff', borderRadius: 3 }} />} label="Run running" />
      </Space>

      {hitLimit && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          title={`Showing the most recent ${RUNS_FETCH_LIMIT} runs — older actual runs may not appear. Projections are unaffected.`}
        />
      )}

      {chartBody}
    </Modal>
  );
}

// ── Per-connection schedule computation ───────────────────────────────
// Extracted from ScheduleTimelineDialog's `schedByConn` useMemo (pure
// function of connections + window, no component state).

// Union of every per-stream series' occurrences for one connection,
// deduplicated and time-sorted (ticks shared across tables that inherit the
// same cron collapse to one union entry rather than being drawn twice).
function unionTickTimes(perStream) {
  const set = new Set();
  for (const s of perStream) {
    for (const t of s.occ) {set.add(t);}
  }
  return Array.from(set).sort((a, b) => a - b);
}

function computeConnSchedule(conn, windowStartMs, windowEndMs, cap) {
  const cache = new Map();
  const occOf = (cron) => {
    if (!cache.has(cron)) {cache.set(cron, cronOccurrences(cron, windowStartMs, windowEndMs, cap));}
    return cache.get(cron);
  };
  const perStream = buildStreamSchedules(conn)
    .map((s) => ({ ...s, occ: occOf(s.cron) }))
    .filter((s) => s.occ.length);

  let unionTicks;
  if (perStream.length) {
    unionTicks = unionTickTimes(perStream);
  } else {
    // No per-table metadata — fall back to the connection-level schedule.
    const connCron = (conn.schedule || '').trim();
    unionTicks = connCron ? occOf(connCron) : [];
  }
  if (!unionTicks.length && !perStream.length) {return null;}
  return { unionTicks, streams: perStream };
}

// connId -> { unionTicks, streams: [{stream,cron,occ,overridden}] }
export function computeSchedByConn(connections, windowStartMs, windowEndMs, cap = PROJECTION_CAP) {
  const out = {};
  for (const conn of connections) {
    const sched = computeConnSchedule(conn, windowStartMs, windowEndMs, cap);
    if (sched) {out[conn.id] = sched;}
  }
  return out;
}

// ── Gantt row-building ────────────────────────────────────────────────
// Extracted from ScheduleTimelineDialog's `ganttRows` useMemo.
function filterRunsByStatus(connRuns, statusFLower) {
  if (!statusFLower) {return connRuns;}
  return connRuns.filter((r) => (r.status || '').toLowerCase() === statusFLower);
}

function buildConnGroupRow(conn, sched, connRuns, isExpanded, showProjections, showRuns) {
  return {
    id: conn.id,
    connId: conn.id,
    label: conn.name,
    sub: false,
    connector: conn.connector_name,
    ticks: showProjections ? sched.unionTicks : [],
    cron: null,
    runs: showRuns ? connRuns : [],
    expandable: sched.streams.length > 0,
    expanded: isExpanded,
    streamCount: sched.streams.length,
  };
}

function buildStreamSubRows(conn, sched) {
  return sched.streams.map((s) => ({
    id: `${conn.id}::${s.stream}`,
    connId: conn.id,
    label: `↳ ${s.stream}${s.overridden ? ' · custom' : ''}`,
    sub: true,
    connector: conn.connector_name,
    ticks: s.occ,
    cron: s.cron,
    runs: [],
    expandable: false,
  }));
}

// Ordering key: the connection's intrinsic earliest activity from the FULL
// data (all projected firings + all runs in window), independent of the
// Projections / Actual-runs / status toggles — so flipping a toggle never
// reshuffles the rows.
function earliestActivity(sched, allRuns) {
  let earliest = Infinity;
  if (sched.unionTicks.length) {earliest = Math.min(earliest, sched.unionTicks[0]);}
  for (const r of allRuns) {earliest = Math.min(earliest, r.startMs);}
  return earliest;
}

function buildConnRowGroup(conn, schedByConn, runsByConn, expanded, statusFLower, showProjections, showRuns) {
  const sched = schedByConn[conn.id] || { unionTicks: [], streams: [] };
  const allRuns = runsByConn[conn.id] || [];
  const connRuns = filterRunsByStatus(allRuns, statusFLower);
  const expandable = sched.streams.length > 0;
  const isExpanded = expandable && expanded.has(conn.id);

  const group = [buildConnGroupRow(conn, sched, connRuns, isExpanded, showProjections, showRuns)];
  if (isExpanded && showProjections) {group.push(...buildStreamSubRows(conn, sched));}

  return { group, earliest: earliestActivity(sched, allRuns), name: conn.name };
}

export function buildGanttRows(
  connections, schedByConn, runsByConn, filterConns, filterStatus, showProjections, showRuns, expanded,
) {
  const connSet = filterConns.length ? new Set(filterConns) : null;
  const statusFLower = filterStatus ? filterStatus.toLowerCase() : null;
  const built = [];
  for (const conn of connections) {
    if (connSet && !connSet.has(conn.id)) {continue;}
    built.push(buildConnRowGroup(conn, schedByConn, runsByConn, expanded, statusFLower, showProjections, showRuns));
  }
  // Tie-break by name so connections with no activity keep a stable order.
  built.sort((a, b) => (a.earliest - b.earliest) || a.name.localeCompare(b.name));
  return built.flatMap((b) => b.group);
}

// ── Gantt chart (canvas-based) ───────────────────────────────────────
function GanttChart({
  rows, windowStartMs, windowEndMs, onToggleExpand,
  sensorByConnector = {}, daemonHealthy = true, onSchedulerChanged,
}) {
  const wrapRef = useRef(null);
  const colRef = useRef(null);
  const canvasRef = useRef(null);
  const [chartW, setChartW] = useState(DEFAULT_CHART_W);
  const [tip, setTip] = useState(null); // { x, y, lines: string[], color }
  const [hoverRow, setHoverRow] = useState(null);

  // Theme-aware palette. CSS vars can't be read inside canvas
  // fillStyle/strokeStyle, so the on-canvas colours are resolved here from
  // the active theme; the DOM label gutter reuses the same values so the
  // label rows stay visually aligned with the canvas zebra striping.
  const { themeConfig } = useTheme();
  const isDark = themeConfig?.mode === 'dark';
  const cpal = useMemo(() => (isDark ? {
    rowSub: '#1b2740', rowEven: '#1f1f24', rowOdd: '#26262c',
    hover: 'rgba(22,119,255,0.18)', weekend: 'rgba(255,255,255,0.05)',
    gridMinor: '#3a3a42', gridMajor: '#55555f', tickPast: '#5c6675',
    barShadow: 'rgba(0,0,0,0.5)', sheen: 'rgba(255,255,255,0.10)',
  } : {
    rowSub: '#f6f9ff', rowEven: '#ffffff', rowOdd: '#fbfbfc',
    hover: 'rgba(22,119,255,0.07)', weekend: 'rgba(15,23,42,0.035)',
    gridMinor: '#f0f0f0', gridMajor: '#dcdcdc', tickPast: '#c2c8d0',
    barShadow: 'rgba(0,0,0,0.12)', sheen: 'rgba(255,255,255,0.22)',
  }), [isDark]);

  const totalH = rows.length * ROW_H;
  const span = Math.max(1, windowEndMs - windowStartMs);
  const scale = useCallback((ts) => ((ts - windowStartMs) / span) * chartW, [windowStartMs, span, chartW]);

  // Weekend bands — only for reasonably short windows, else they'd be clutter.
  const weekends = useMemo(() => {
    const days = (windowEndMs - windowStartMs) / MS_PER_DAY;
    if (days > WEEKEND_BAND_MAX_DAYS) {return [];}
    const out = [];
    let d = dayjs(windowStartMs).startOf('day');
    let guard = 0;
    while (d.valueOf() <= windowEndMs && guard < WEEKEND_BAND_GUARD) {
      const dow = d.day(); // 0 Sun … 6 Sat
      if (dow === 0 || dow === SATURDAY) {out.push([d.valueOf(), d.add(1, 'day').valueOf()]);}
      d = d.add(1, 'day');
      guard += 1;
    }
    return out;
  }, [windowStartMs, windowEndMs]);

  // Track the chart column's pixel width.
  useEffect(() => {
    const el = colRef.current;
    if (!el) {return undefined;}
    const measure = () => setChartW(Math.max(MIN_CHART_W, el.clientWidth));
    measure();
    if (typeof ResizeObserver === 'undefined') {return undefined;}
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Lay out marks once per (rows, scale) — reused for drawing AND hit-testing.
  const laid = useMemo(() => rows.map((r) => {
    const bars = r.runs.map((run) => ({
      x1: scale(run.startMs),
      x2: Math.max(scale(run.startMs) + MIN_BAR_WIDTH, scale(run.endMs)),
      run,
    }));
    // Downsample ticks to one per horizontal pixel — beyond that they'd
    // visually merge anyway, and it bounds work to ~chartW per row.
    const ticks = [];
    let lastPx = -2;
    for (const ts of r.ticks) {
      const x = scale(ts);
      const px = Math.round(x);
      if (px === lastPx) {continue;}
      lastPx = px;
      ticks.push({ x, ts, future: ts >= Date.now() });
    }
    return { bars, ticks };
  }), [rows, scale]);

  // Adaptive axis ticks: daily for short windows, weekly for medium, monthly
  // for long. `major` ticks (month boundaries / months) get a stronger
  // gridline. Labels carry the date so the axis isn't just month names.
  const axisTicks = useMemo(
    () => computeAxisTicks(windowStartMs, windowEndMs),
    [windowStartMs, windowEndMs],
  );

  const nowMs = Date.now();
  const nowX = nowMs >= windowStartMs && nowMs <= windowEndMs ? scale(nowMs) : null;

  // Draw.
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) {return;}
    const dpr = window.devicePixelRatio || 1;
    cv.width = Math.max(1, Math.floor(chartW * dpr));
    cv.height = Math.max(1, Math.floor(totalH * dpr));
    cv.style.width = `${chartW}px`;
    cv.style.height = `${totalH}px`;
    const ctx = cv.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, chartW, totalH);

    drawRowBackgrounds(ctx, rows, chartW, cpal, hoverRow);
    drawWeekendBands(ctx, weekends, scale, totalH, cpal);
    drawAxisGridlines(ctx, axisTicks, scale, totalH, cpal);
    drawRowMarks(ctx, laid, cpal);
    drawNowLine(ctx, nowX, totalH);
  }, [laid, rows, axisTicks, weekends, hoverRow, chartW, totalH, scale, nowX, cpal]);

  // Hit-testing for tooltip + click.
  const hitAt = useCallback((clientX, clientY) => {
    const cv = canvasRef.current;
    if (!cv) {return null;}
    const rect = cv.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const rowIdx = Math.floor(y / ROW_H);
    if (rowIdx < 0 || rowIdx >= laid.length) {return null;}
    const lm = laid[rowIdx];
    for (const b of lm.bars) {
      if (x >= b.x1 - 1 && x <= b.x2 + 1) {return { kind: 'run', rowIdx, b };}
    }
    for (const t of lm.ticks) {
      if (Math.abs(x - t.x) <= TICK_HIT_PX) {return { kind: 'tick', rowIdx, t };}
    }
    return null;
  }, [laid]);

  const rowIndexAt = useCallback((clientY) => {
    const cv = canvasRef.current;
    if (!cv) {return null;}
    const idx = Math.floor((clientY - cv.getBoundingClientRect().top) / ROW_H);
    return idx >= 0 && idx < rows.length ? idx : null;
  }, [rows.length]);

  const onMove = (e) => {
    const cv = canvasRef.current;
    const hit = hitAt(e.clientX, e.clientY);
    const rowIdx = hit ? hit.rowIdx : rowIndexAt(e.clientY);
    const row = rowIdx != null ? rows[rowIdx] : null;
    setHoverRow(rowIdx);

    // A hit always lands on a row; off every row there is nothing to point
    // at or describe (and the helpers below read row.label / row.expandable).
    if (!row) {
      if (cv) {cv.style.cursor = 'default';}
      setTip(null);
      return;
    }
    if (cv) {cv.style.cursor = cursorForHit(hit, row);}
    if (!hit) {
      setTip(null);
      return;
    }
    const { lines, color } = tooltipForHit(hit, row);
    setTip({ x: e.clientX, y: e.clientY, lines, color });
  };

  const onClick = (e) => {
    const hit = hitAt(e.clientX, e.clientY);
    // Clicking a run bar opens it in Dagit — but only where the proxy is
    // mounted. With it off we deliberately fall THROUGH to the row-expand
    // branch below rather than returning, so a click still does what the
    // pointer cursor promises instead of nothing at all.
    if (isDagitEnabled() && hit && hit.kind === 'run' && hit.b.run.runId) {
      openDagsterRun(hit.b.run.runId);
      return;
    }
    const rowIdx = hit ? hit.rowIdx : rowIndexAt(e.clientY);
    const row = rowIdx != null ? rows[rowIdx] : null;
    if (row && row.expandable) {onToggleExpand(row.connId);}
  };

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
      border: '1px solid var(--theme-border, #ebedf0)', borderRadius: 10, overflow: 'hidden',
      background: 'var(--theme-contentBg, #fff)', boxShadow: '0 1px 3px var(--theme-shadow, rgba(0,0,0,0.04))',
    }}>
      {/* Axis header */}
      <div style={{ display: 'flex', height: HEADER_H, borderBottom: CARD_BORDER, flex: 'none', background: 'var(--theme-backgroundSecondary, #fafbfc)' }}>
        <div style={{
          width: LABEL_W, flex: 'none', borderRight: CARD_BORDER,
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 10,
        }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, color: THEME_TEXT_ICON, textTransform: 'uppercase' }}>
            Connection
          </span>
        </div>
        <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
          {axisTicks.map((m) => (
            <span
              key={m.ts}
              style={{
                position: 'absolute', left: scale(m.ts) + 3, top: 7,
                fontSize: 11, whiteSpace: 'nowrap',
                color: m.major ? 'var(--theme-textPrimary, #262626)' : THEME_TEXT_ICON,
                fontWeight: m.major ? 600 : 400,
              }}
            >
              {m.label}
            </span>
          ))}
          {nowX != null && (
            <div style={{ position: 'absolute', left: nowX, top: 0, transform: 'translateX(-50%)', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#ff4d4f', fontWeight: 700, lineHeight: 1 }}>Today</div>
              <div style={{
                width: 0, height: 0, margin: '2px auto 0',
                borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                borderTop: '6px solid #ff4d4f',
              }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Body: labels + canvas, scroll together vertically */}
      <div ref={wrapRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
        <div style={{ display: 'flex' }}>
          <div
            style={{ width: LABEL_W, flex: 'none', borderRight: CARD_BORDER }}
            onMouseLeave={() => setHoverRow(null)}
          >
            {rows.map((r, i) => {
              const zebra = rowZebraColor(r, i, cpal);
              return (
                <div
                  key={r.id}
                  title={rowLabelTitle(r)}
                  onClick={r.expandable ? () => onToggleExpand(r.connId) : undefined}
                  onMouseEnter={() => setHoverRow(i)}
                  style={rowLabelStyle(r, hoverRow === i, zebra, cpal, isDark)}
                >
                  {!r.sub && r.connector ? (
                    <span style={{ flex: 'none' }}>
                      <SchedulerControl
                        connector={connectorSensorKey(r.connector)}
                        sensor={sensorByConnector[connectorSensorKey(r.connector)]}
                        daemonHealthy={daemonHealthy}
                        onChanged={onSchedulerChanged}
                      />
                    </span>
                  ) : null}
                  <span style={{ flex: 1, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.label}
                    {r.expandable && r.streamCount ? (
                      <span style={{ color: '#bfbfbf', fontWeight: 400 }}> ({r.streamCount})</span>
                    ) : null}
                  </span>
                  <span style={{ flex: 'none', width: 12, textAlign: 'center', color: THEME_TEXT_ICON, fontSize: 10 }}>
                    {rowCaret(r)}
                  </span>
                </div>
              );
            })}
          </div>
          <div ref={colRef} style={{ position: 'relative', flex: 1 }}>
            <canvas
              ref={canvasRef}
              onMouseMove={onMove}
              onMouseLeave={() => { setTip(null); setHoverRow(null); }}
              onClick={onClick}
              style={{ display: 'block' }}
            />
          </div>
        </div>
      </div>

      {tip && (
        <div
          style={{
            position: 'fixed', left: tip.x + 14, top: tip.y + 14, zIndex: 2000,
            background: 'rgba(0,0,0,0.82)', color: '#fff', padding: '6px 10px',
            borderRadius: 6, fontSize: 12, maxWidth: 320, pointerEvents: 'none',
            borderLeft: `3px solid ${tip.color}`,
          }}
        >
          {tip.lines.map((ln, i) => (
            <div key={i} style={{ fontWeight: i === 0 ? 600 : 400, opacity: i === 0 ? 1 : 0.85, whiteSpace: 'nowrap' }}>
              {ln}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, h / 2, w / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

// ── Axis-tick computation ─────────────────────────────────────────────
// Extracted from GanttChart's `axisTicks` useMemo. Adaptive granularity:
// daily for short windows, weekly for medium, monthly for long.
function axisUnitStep(days) {
  if (days <= AXIS_DAILY_MAX_DAYS) {return { unit: 'day', step: 1 };}
  if (days <= AXIS_WEEKLY_MAX_DAYS) {return { unit: 'day', step: 7 };}
  return { unit: 'month', step: 1 };
}

function axisTickLabel(cur, unit, step, isMonthStart) {
  if (unit === 'month') {
    return cur.format('MMM') + (cur.month() === 0 ? ` ’${cur.format('YY')}` : '');
  }
  if (step === 1) {
    return isMonthStart ? cur.format('MMM D') : cur.format('D');
  }
  return cur.format('MMM D');
}

export function computeAxisTicks(windowStartMs, windowEndMs) {
  const days = (windowEndMs - windowStartMs) / MS_PER_DAY;
  const { unit, step } = axisUnitStep(days);

  const out = [];
  let cur = dayjs(windowStartMs).startOf(unit === 'month' ? 'month' : 'day');
  while (cur.valueOf() < windowStartMs) {cur = cur.add(step, unit);}
  let guard = 0;
  while (cur.valueOf() <= windowEndMs && guard < AXIS_TICK_GUARD) {
    const isMonthStart = cur.date() === 1;
    const label = axisTickLabel(cur, unit, step, isMonthStart);
    out.push({ ts: cur.valueOf(), label, major: unit === 'month' || isMonthStart });
    cur = cur.add(step, unit);
    guard += 1;
  }
  return out;
}

// ── Canvas drawing (extracted from GanttChart's draw useEffect) ───────
// Each function owns one visual layer and mutates the given 2D context in
// place; none read component state directly, so they're callable/testable
// with a stub ctx.
export function drawRowBackgrounds(ctx, rows, chartW, cpal, hoverRow) {
  for (let i = 0; i < rows.length; i += 1) {
    const y = i * ROW_H;
    if (rows[i].sub) {ctx.fillStyle = cpal.rowSub;}
    else {ctx.fillStyle = i % 2 === 0 ? cpal.rowEven : cpal.rowOdd;}
    ctx.fillRect(0, y, chartW, ROW_H);
  }
  if (hoverRow != null && hoverRow >= 0 && hoverRow < rows.length) {
    ctx.fillStyle = cpal.hover;
    ctx.fillRect(0, hoverRow * ROW_H, chartW, ROW_H);
  }
}

export function drawWeekendBands(ctx, weekends, scale, totalH, cpal) {
  ctx.fillStyle = cpal.weekend;
  for (const [a, b] of weekends) {
    const x1 = scale(a);
    const x2 = scale(b);
    ctx.fillRect(x1, 0, Math.max(1, x2 - x1), totalH);
  }
}

// Minor (solid, light) gridlines first, then major (dashed, darker) —
// drawn as two passes so major lines always sit visually on top.
export function drawAxisGridlines(ctx, axisTicks, scale, totalH, cpal) {
  ctx.strokeStyle = cpal.gridMinor;
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  for (const m of axisTicks) {
    if (m.major) {continue;}
    const x = Math.round(scale(m.ts)) + 0.5;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, totalH);
    ctx.stroke();
  }
  ctx.strokeStyle = cpal.gridMajor;
  ctx.setLineDash([4, 4]);
  for (const m of axisTicks) {
    if (!m.major) {continue;}
    const x = Math.round(scale(m.ts)) + 0.5;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, totalH);
    ctx.stroke();
  }
  ctx.setLineDash([]);
}

export function drawRowMarks(ctx, laid, cpal) {
  for (let i = 0; i < laid.length; i += 1) {
    const cy = i * ROW_H + ROW_H / 2;
    const { bars, ticks } = laid[i];
    // Ticks (behind bars) — rounded caps.
    for (const t of ticks) {
      ctx.fillStyle = t.future ? '#1677ff' : cpal.tickPast;
      roundRect(ctx, Math.round(t.x) - TICK_RADIUS, cy - TICK_H / 2, TICK_WIDTH, TICK_H, TICK_RADIUS);
      ctx.fill();
    }
    // Bars — filled + subtle top highlight + hairline border.
    for (const b of bars) {
      const meta = runStatusMeta(b.run.status);
      const w = Math.max(MIN_BAR_WIDTH, b.x2 - b.x1);
      const y = cy - BAR_H / 2;
      ctx.save();
      ctx.shadowColor = cpal.barShadow;
      ctx.shadowBlur = 2;
      ctx.shadowOffsetY = 1;
      ctx.fillStyle = meta.color;
      roundRect(ctx, b.x1, y, w, BAR_H, BAR_RADIUS);
      ctx.fill();
      ctx.restore();
      // top sheen
      ctx.fillStyle = cpal.sheen;
      roundRect(ctx, b.x1, y, w, BAR_H / 2, BAR_RADIUS);
      ctx.fill();
    }
  }
}

export function drawNowLine(ctx, nowX, totalH) {
  if (nowX == null) {return;}
  const x = Math.round(nowX) + 0.5;
  ctx.save();
  ctx.strokeStyle = '#ff4d4f';
  ctx.lineWidth = 1.5;
  ctx.shadowColor = 'rgba(255,77,79,0.45)';
  ctx.shadowBlur = 4;
  ctx.beginPath();
  ctx.moveTo(x, 0);
  ctx.lineTo(x, totalH);
  ctx.stroke();
  ctx.restore();
}

// ── Hover tooltip + cursor (extracted from GanttChart's onMove) ───────
export function tooltipForHit(hit, row) {
  if (hit.kind === 'run') {
    const meta = runStatusMeta(hit.b.run.status);
    const triggeredBy = hit.b.run.triggeredBy ? ` · ${hit.b.run.triggeredBy}` : '';
    const runLines = [
      row.label,
      `Run · ${meta.label}${triggeredBy}`,
      `${dayjs(hit.b.run.startMs).format('MMM D, HH:mm')} → ${dayjs(hit.b.run.endMs).format('MMM D, HH:mm')}`,
      hit.b.run.runId ? 'Click to open in Dagit' : '',
    ].filter(Boolean);
    return { lines: runLines, color: meta.color };
  }
  const lines = [
    row.label,
    `Scheduled${hit.t.future ? '' : ' (past)'} · ${dayjs(hit.t.ts).format('ddd MMM D, HH:mm')}`,
    row.cron ? `cron: ${row.cron}` : '',
  ].filter(Boolean);
  return { lines, color: hit.t.future ? '#1677ff' : '#bfbfbf' };
}

export function cursorForHit(hit, row) {
  if (!hit) {return row && row.expandable ? 'pointer' : 'default';}
  if (hit.kind === 'run' && hit.b.run.runId) {return 'pointer';}
  return row.expandable ? 'pointer' : 'default';
}

// ── Row-label styling (extracted from GanttChart's label-column render) ─
export function rowZebraColor(row, i, cpal) {
  if (row.sub) {return cpal.rowSub;}
  return i % 2 === 0 ? cpal.rowEven : cpal.rowOdd;
}

export function rowLabelColor(row, isDark) {
  if (row.sub) {return isDark ? '#9aa7bd' : '#7a8699';}
  return isDark ? '#dbe4f5' : '#1f3a68';
}

export function rowLabelTitle(row) {
  if (!row.expandable) {return row.label;}
  return `${row.label} — click to ${row.expanded ? 'collapse' : 'expand'} ${row.streamCount} table schedule${row.streamCount === 1 ? '' : 's'}`;
}

// A label row's inline style: sub-rows indent and lighten; hover wins over
// the zebra stripe.
export function rowLabelStyle(row, hovered, zebra, cpal, isDark) {
  return {
    height: ROW_H, paddingRight: 8, paddingLeft: row.sub ? SUB_ROW_INDENT : ROW_INDENT,
    fontSize: 12.5,
    color: rowLabelColor(row, isDark),
    fontWeight: row.sub ? FONT_WEIGHT_SUB : FONT_WEIGHT_ROW,
    display: 'flex', alignItems: 'center', gap: 4,
    cursor: row.expandable ? 'pointer' : 'default',
    background: hovered ? cpal.hover : zebra,
    transition: 'background 0.08s',
  };
}

export function rowCaret(row) {
  if (!row.expandable) {return '';}
  return row.expanded ? '▾' : '▸';
}

function Legend({ swatch, label }) {
  return (
    <Space size={6}>
      {swatch}
      <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
    </Space>
  );
}

function StatCard({ icon, color, label, value, tooltip }) {
  const card = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
      background: 'var(--theme-contentBg, #fff)', border: CARD_BORDER, borderRadius: 10,
      minWidth: 156, boxShadow: '0 1px 2px var(--theme-shadow, rgba(0,0,0,0.03))',
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 9, flex: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${color}14`, color, fontSize: 17,
      }}>
        {icon}
      </div>
      <div style={{ lineHeight: 1.2 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--theme-textPrimary, #1f1f1f)' }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--theme-textSecondary, #8c8c8c)' }}>{label}</div>
      </div>
    </div>
  );
  return tooltip ? <Tooltip title={tooltip}>{card}</Tooltip> : card;
}

// ── Status helpers ───────────────────────────────────────────────────
const RUN_STATUS = {
  success:     { color: '#52c41a', label: 'Success' },
  failure:     { color: '#ff4d4f', label: 'Failed' },
  started:     { color: '#1677ff', label: 'Running' },
  starting:    { color: '#1677ff', label: 'Starting' },
  queued:      { color: '#bfbfbf', label: 'Queued' },
  not_started: { color: '#bfbfbf', label: 'Queued' },
  canceled:    { color: '#8c8c8c', label: 'Canceled' },
  canceling:   { color: '#8c8c8c', label: 'Canceling' },
};
export function runStatusMeta(status) {
  return RUN_STATUS[(status || '').toLowerCase()] || { color: '#bfbfbf', label: status || 'unknown' };
}

// ── Schedule model ───────────────────────────────────────────────────
export function streamNamesOf(conn) {
  if (Array.isArray(conn.selected_streams) && conn.selected_streams.length) {return conn.selected_streams;}
  if (Array.isArray(conn.streams)) {return conn.streams.map((s) => s.name).filter(Boolean);}
  return [];
}

/**
 * Resolve a connection into its distinct firing series. Streams listed in
 * `stream_schedules` with a non-empty cron fire on their own cadence (one
 * series each, grouped by cron); every other (inheriting) stream rides the
 * connection-level `schedule`. Manual connections return [].
 */
// Splits stream-level cron overrides into cron -> [streams] groups, and the
// set of stream names that carry an override (so the connection-level
// schedule knows which streams still inherit it).
function groupOverridesByCron(overrides) {
  const byCron = {};
  const overriddenSet = new Set();
  for (const [stream, cron] of Object.entries(overrides)) {
    const c = (cron || '').trim();
    if (!c) {continue;}
    overriddenSet.add(stream);
    if (!byCron[c]) {byCron[c] = [];}
    byCron[c].push(stream);
  }
  return { byCron, overriddenSet };
}

// The connection-level schedule's own entry, covering whichever streams
// don't have their own override — or null when there's nothing for it to
// cover (no connection cron, or every stream is overridden).
function connectionLevelScheduleEntry(connCron, allStreams, overriddenSet) {
  if (!connCron) {return null;}
  if (allStreams.length === 0) {
    return { cron: connCron, scope: 'connection', streams: null, label: 'Scheduled sync' };
  }
  const inheriting = allStreams.filter((s) => !overriddenSet.has(s));
  if (inheriting.length === 0) {return null;}
  const coversAll = inheriting.length === allStreams.length;
  const streamPlural = inheriting.length === 1 ? '' : 's';
  return {
    cron: connCron,
    scope: 'connection',
    streams: inheriting,
    label: coversAll ? 'Scheduled sync' : `Scheduled sync · ${inheriting.length} stream${streamPlural}`,
  };
}

export function buildEffectiveSchedules(conn) {
  const connCron = (conn.schedule || '').trim();
  const overrides = conn.stream_schedules || {};
  const allStreams = streamNamesOf(conn);

  const { byCron, overriddenSet } = groupOverridesByCron(overrides);
  const out = Object.entries(byCron).map(([cron, streams]) => (
    { cron, scope: 'stream', streams, label: `↳ ${streams.join(', ')}` }
  ));
  const connEntry = connectionLevelScheduleEntry(connCron, allStreams, overriddenSet);
  if (connEntry) {out.push(connEntry);}
  return out;
}

/**
 * Per-table effective schedule: one entry per stream that actually has a
 * schedule. A stream uses its `stream_schedules` override when set, otherwise
 * it inherits the connection-level `schedule`. Streams with neither (manual)
 * are omitted. Returns [] when the connection exposes no stream metadata.
 */
export function buildStreamSchedules(conn) {
  const connCron = (conn.schedule || '').trim();
  const overrides = conn.stream_schedules || {};
  const out = [];
  for (const stream of streamNamesOf(conn)) {
    const override = (overrides[stream] || '').trim();
    const cron = override || connCron;
    if (cron) {out.push({ stream, cron, overridden: !!override });}
  }
  return out;
}

// ── Minimal 5-field cron evaluator (UTC) ─────────────────────────────
// Supports *, lists (a,b), ranges (a-b), and steps (*/n, a-b/n, a/n).
// Resolves one comma-separated part of a cron field (e.g. `"*/15"`,
// `"1-5"`, `"3"`) into its {lo, hi, step} range. NaN lo/hi signals an
// unparseable part, which the caller skips.
function parseCronPart(part, min, max) {
  let step = 1;
  let range = part;
  const si = part.indexOf('/');
  if (si !== -1) {
    step = parseInt(part.slice(si + 1), 10);
    if (!Number.isFinite(step) || step < 1) {step = 1;}
    range = part.slice(0, si);
  }
  let lo;
  let hi;
  if (range === '*' || range === '') {
    lo = min; hi = max;
  } else if (range.indexOf('-') !== -1) {
    const [a, b] = range.split('-');
    lo = parseInt(a, 10); hi = parseInt(b, 10);
  } else {
    lo = parseInt(range, 10);
    hi = si !== -1 ? max : lo;
  }
  return { lo, hi, step };
}

function addCronPart(set, part, min, max) {
  const { lo, hi, step } = parseCronPart(part, min, max);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {return;}
  for (let v = Math.max(lo, min); v <= Math.min(hi, max); v += step) {set.add(v);}
}

export function parseCronField(field, min, max) {
  const set = new Set();
  for (const part of String(field).trim().split(',')) {
    if (part) {addCronPart(set, part, min, max);}
  }
  return set;
}

// Day-of-month/day-of-week combine with cron's "OR when both are
// restricted" rule: if either field was left as `*` it's not restrictive,
// so the other one alone decides; if both are restricted, either matching
// is enough; if neither is restricted, every day matches.
export function dayMatches(d, doms, dows, domR, dowR) {
  const domOk = doms.has(d.getUTCDate());
  const dowOk = dows.has(d.getUTCDay());
  if (domR && dowR) {return domOk || dowOk;}
  if (domR) {return domOk;}
  if (dowR) {return dowOk;}
  return true;
}

// Parse all 5 cron fields; a `dow` field matching 7 (some crons' Sunday)
// also sets the standard 0.
function parseCronFields(parts) {
  const [minP, hourP, domP, monP, dowP] = parts;
  const mins = parseCronField(minP, 0, CRON_MINUTE_MAX);
  const hours = parseCronField(hourP, 0, CRON_HOUR_MAX);
  const doms = parseCronField(domP, 1, CRON_DOM_MAX);
  const mons = parseCronField(monP, 1, CRON_MONTH_MAX);
  const dows = parseCronField(dowP, 0, CRON_DOW_MAX);
  if (dows.has(CRON_DOW_MAX)) {dows.add(0);}
  return { mins, hours, doms, mons, dows };
}

// Does minute `d` satisfy every cron field (day-of-month/day-of-week via
// dayMatches' OR-when-both-restricted rule)?
function matchesCronFields(d, { mins, hours, mons, doms, dows, domR, dowR }) {
  return mins.has(d.getUTCMinutes()) && hours.has(d.getUTCHours()) && mons.has(d.getUTCMonth() + 1)
    && dayMatches(d, doms, dows, domR, dowR);
}

export function cronOccurrences(cron, fromMs, toMs, cap = PROJECTION_CAP) {
  const parts = String(cron || '').trim().split(/\s+/);
  if (parts.length !== CRON_FIELD_COUNT) {return [];}
  const fields = parseCronFields(parts);
  if (![fields.mins, fields.hours, fields.doms, fields.mons, fields.dows].every((s) => s.size)) {return [];}
  fields.domR = parts[2].trim() !== '*';
  fields.dowR = parts[4].trim() !== '*';

  const out = [];
  let t = Math.ceil(fromMs / MS_PER_MINUTE) * MS_PER_MINUTE;
  let iter = 0;
  const MAX_ITER = 800000;
  while (t <= toMs && out.length < cap && iter < MAX_ITER) {
    const d = new Date(t);
    if (matchesCronFields(d, fields)) {
      out.push(t);
    }
    t += MS_PER_MINUTE;
    iter += 1;
  }
  return out;
}
